/**
 *  @class
 *  @function ProductRecommendations
 */
if (!customElements.get('product-recommendations')) {
	class ProductRecommendations extends HTMLElement {
		constructor() {
			super();

			this.parent = this.closest('.product-recommendations--parent');

			if (this.classList.contains('cart-recommendations')) {
				this.cart = true;
				this.parent = this.closest('.cart-recommendations--parent');
			}
		}
		connectedCallback() {
			if (this.requestStarted) return;
			this.fetchProducts();
		}
		disconnectedCallback() {
			this.clearCompleteSpaceWait();
			if (this.requestController) this.requestController.abort();
			this.requestController = null;
			this.requestStarted = false;
			this.pendingRecommendations = null;
		}
		async fetchProducts() {
			this.requestStarted = true;
			const requestController = new AbortController();
			this.requestController = requestController;

			try {
				const response = await fetch(this.dataset.url, {
					signal: requestController.signal
				});
				if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

				const text = await response.text();
				if (requestController.signal.aborted) return;

				const html = document.createElement('div');
				html.innerHTML = text;
				const recommendations = html.querySelector('product-recommendations');

				if (!recommendations || !recommendations.innerHTML.trim().length) {
					this.collapseRecommendations();
					return;
				}

				this.prepareRecommendationSet(recommendations.innerHTML);
			} catch (error) {
				if (error.name === 'AbortError') return;
				this.collapseRecommendations();
				console.error(error);
			}
		}
		prepareRecommendationSet(markup) {
			const completeSpace = document.querySelector('complete-space-recommendations');
			if (this.cart || !this.dataset.displayLimit || !completeSpace || completeSpace.dataset.state !== 'loading') {
				this.renderRecommendations(markup);
				return;
			}

			// Keep reserve cards detached until the buying rows resolve. This lets us
			// deduplicate before <theme-scroll> caches its slide collection.
			this.pendingRecommendations = markup;
			this.onCompleteSpaceResolved = () => {
				this.clearCompleteSpaceWait();
				if (!this.pendingRecommendations) return;

				const pendingRecommendations = this.pendingRecommendations;
				this.pendingRecommendations = null;
				this.renderRecommendations(pendingRecommendations);
			};
			document.addEventListener('complete-space:resolved', this.onCompleteSpaceResolved);
		}
		clearCompleteSpaceWait() {
			if (!this.onCompleteSpaceResolved) return;

			document.removeEventListener('complete-space:resolved', this.onCompleteSpaceResolved);
			this.onCompleteSpaceResolved = null;
		}
		renderRecommendations(markup) {
			const recommendationRoot = document.createElement('div');
			recommendationRoot.innerHTML = markup;
			const hasRecommendations = this.finalizeRecommendationSet(recommendationRoot);

			if (!hasRecommendations) {
				this.collapseRecommendations();
				return;
			}

			this.hidden = false;
			this.innerHTML = recommendationRoot.innerHTML;

			if (this.parent) {
				if (this.cart) {
					this.parent.classList.add('cart-recommendations--full');
				} else {
					this.parent.classList.add('product-recommendations--full');
				}
			}

			const lazyLoader = window.lazySizes?.loader;
			if (lazyLoader?.unveil) {
				this.querySelectorAll('.lazyload').forEach((image) => lazyLoader.unveil(image));
			}

			this.classList.add('product-recommendations--loaded');
		}
		finalizeRecommendationSet(root) {
			if (this.cart || !this.dataset.displayLimit) return Boolean(root.innerHTML.trim());

			const excludedHandles = new Set(
				Array.from(document.querySelectorAll(
					'complete-space-recommendations[data-state="ready"] [data-complete-space-item][data-product-handle]'
				)).map((item) => item.dataset.productHandle)
			);
			const displayLimit = Number.parseInt(this.dataset.displayLimit, 10);
			const slides = Array.from(root.querySelectorAll('.carousel__slide[data-product-handle]'));
			let visibleProducts = 0;

			slides.forEach((slide) => {
				const isDuplicate = excludedHandles.has(slide.dataset.productHandle);
				if (isDuplicate || visibleProducts >= displayLimit) {
					slide.remove();
					return;
				}

				visibleProducts += 1;
			});

			return visibleProducts > 0;
		}
		collapseRecommendations() {
			this.clearCompleteSpaceWait();
			this.pendingRecommendations = null;
			this.hidden = true;
			this.replaceChildren();
			this.classList.add('product-recommendations--loaded');
			if (this.parent) {
				this.parent.classList.remove('product-recommendations--full', 'cart-recommendations--full');
			}
		}
	}

	customElements.define('product-recommendations', ProductRecommendations);
}
